import Button from '@/app/components/common/Button'
import { SanityData } from '@/app/components/Header'
import { DisplayData } from '@/app/components/LocationDataHandler'
import { Icons } from '@/app/components/ui/Icons'
import {
	AvailableInputTypes,
	DEFAULT_NUMBER_OF_GUESTS,
	defaultCheckInDate,
	defaultCheckOutDate,
	INPUT,
	ROOM_GUESTS_SEPARATOR,
	SM_BREAKPOINT,
} from '@/app/constants'
import eventBus from '@/app/eventBus'
import { emitSmallBookingClickGAEvent } from '@/app/ga-event-helpers'
import { PS_EVENTS } from '@/app/ps-events'
import { Allocation, SearchItem } from '@/app/types'
import { getValidDateFromParams, isCountry, smoothScrollTo } from '@/app/utils'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { format } from 'date-fns'
import { startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'
import { FC, useEffect, useRef } from 'react'

interface CompactBookingProps {
	displayValue: DisplayData
	defaultLocation: SearchItem | null
	isMainPage: boolean
	sanityData?: SanityData
	loading?: boolean
	customHeading?: boolean
	isHotelPage?: boolean
	isExpanded?: boolean
	dest?: string[]
	onToggleExpand: (expand: boolean) => void
}

const b = 'compact-booking'
const CompactBooking: FC<CompactBookingProps> = (props) => {
	const { onToggleExpand, isExpanded, dest, displayValue } = props
	const t = useTranslations('Form')
	const isSM = useMediaQuery(SM_BREAKPOINT)
	const compactCalendarInputRef = useRef<HTMLDivElement>(null)
	const calendarOpenedRef = useRef(false)

	const searchParams = useSearchParams()

	const getGuestsCount = (): number => {
		const roomParams: string[] = searchParams.getAll('room')

		if (roomParams.length === 0) {
			return DEFAULT_NUMBER_OF_GUESTS
		}

		const rooms: Allocation[] = roomParams.map((room: string): Allocation => {
			const [adults, children = ''] = room.split(ROOM_GUESTS_SEPARATOR)
			const childAges = children.split(',').filter(Boolean).map(Number)

			return {
				adults: Math.max(parseInt(adults, 10) || 0, 0),
				childAge: childAges.filter((age) => !isNaN(age)),
			}
		})

		return rooms.reduce(
			(total, room) => total + room.adults + room.childAge.length,
			0
		)
	}

	const checkInDate = format(
		getValidDateFromParams(searchParams, 'check-in-date', defaultCheckInDate),
		'MMM dd'
	)
	const checkOutDate = format(
		getValidDateFromParams(searchParams, 'check-out-date', defaultCheckOutDate),
		'MMM dd'
	)

	const handleInputClick = (inputType: AvailableInputTypes) => {
		emitSmallBookingClickGAEvent()

		onToggleExpand(true)

		if (inputType === INPUT.CALENDAR_FROM || inputType === INPUT.CALENDAR_TO) {
			calendarOpenedRef.current = true
		}

		setTimeout(() => {
			eventBus.publish(PS_EVENTS.INPUT_FOCUS, inputType)
		}, 200)
	}

	const handleClose = () => {
		onToggleExpand(false)
	}

	const handleReload = () => {
		smoothScrollTo(0, 0)

		if (typeof window !== 'undefined') {
			setTimeout(() => window.location.reload()), 200
		}
	}

	useEffect(() => {
		const unsubscribe = eventBus.subscribe(
			PS_EVENTS.INPUT_FOCUS,
			(inputType: AvailableInputTypes) => {
				if (isSM && inputType === INPUT.MAIN_FORM) {
					setTimeout(() => onToggleExpand(true), 0)
				}

				if (
					inputType === INPUT.CALENDAR_FROM &&
					compactCalendarInputRef.current &&
					!isExpanded &&
					!calendarOpenedRef.current
				) {
					calendarOpenedRef.current = true
					setTimeout(() => compactCalendarInputRef.current?.click(), 0)
				}
			}
		)

		return () => {
			unsubscribe()
		}
	}, [])

	const getInputValue = () => {
		return isCountry(dest as string[])
			? startCase(displayValue.displayValue)
			: displayValue.displayValue
	}

	return (
		<div>
			<div
				className={classNames(b, {
					[`${b}--expanded`]: isExpanded,
					'max-lg:border-none w-0 h-0 overflow-hidden !p-0': isExpanded,
				})}
			>
				<div
					className={classNames(`${b}__trigger`, {
						[`${b}__trigger--hidden`]: isExpanded,
					})}
					data-testid="compact-booking-trigger"
				>
					{/* Destkop fields */}
					<div className={classNames(`${b}__fields`)}>
						<div
							className={`${b}__field w-[214px] pe-0`}
							onClick={() => handleInputClick(INPUT.LOCATION)}
						>
							<span className="w-full truncate">
								{getInputValue() || t('searchForHotels')}
							</span>
						</div>

						<div className={`${b}__divider`} />

						<div
							className={`${b}__field w-[165px]`}
							onClick={() => handleInputClick(INPUT.CALENDAR_FROM)}
							ref={compactCalendarInputRef}
						>
							{checkInDate} - {checkOutDate}
						</div>

						<div className={`${b}__divider`} />

						<div
							className={`${b}__field w-[130px] pe-0`}
							onClick={() => handleInputClick(INPUT.GUESTS_SELECT)}
						>
							{t('guestsCount', { count: getGuestsCount() })}
						</div>

						<Button
							id="reload-page-button"
							onClick={handleReload}
							name="Reload"
							className={`${b}__search-button `}
						>
							<Icons.iconLens />
						</Button>
					</div>
					{/* Mobile field */}
					<div
						className={`${b}__field ${b}__field--mobile`}
						onClick={() => onToggleExpand(true)}
					>
						<span className="font-semibold w-full truncate">
							{props.displayValue.displayValue || t('searchForHotels')}
						</span>
						<span className="text-xs text-text-700 flex items-center">
							{checkInDate} - {checkOutDate}{' '}
							<span className="w-0.5 h-0.5 bg-text-400 rounded-full flex mx-1" />{' '}
							{t('guestsCount', { count: getGuestsCount() })}
						</span>
					</div>
				</div>
			</div>
		</div>
	)
}

export default dynamic(() => Promise.resolve(CompactBooking), { ssr: false })
